/**
 * EmailAddressInput
 *
 * A tag-chip input for entering multiple email addresses and/or merge tokens
 * (e.g. {client.email}). Used in:
 *   - EmailTemplateEditor (To / CC / BCC fields on templates)
 *   - SendInvoiceStep     (To / CC / BCC at send time)
 *   - ResendInvoiceDialog (To / CC / BCC at resend time)
 *
 * Behaviour:
 *  - Press Enter, Tab, or type a comma/space to commit the current input as a chip
 *  - Click × on a chip to remove it
 *  - Merge tokens (strings starting with "{") are rendered with a distinct style
 *  - Paste of comma-separated addresses creates multiple chips at once
 *  - Fully keyboard accessible
 *
 * TODO (Engineering): When real contacts/clients are available, hook up
 *   `suggestions` prop with a debounced GET /api/contacts?q=... call
 *   for typeahead autocomplete.
 */

import React, { useRef, useState, type KeyboardEvent, type ClipboardEvent } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Returns true if the value looks like a merge token, e.g. {client.email} */
function isMergeToken(val: string): boolean {
  return val.startsWith('{') && val.endsWith('}');
}

/** Loosely validates an email address or accepts merge tokens as valid */
function isValidEntry(val: string): boolean {
  if (isMergeToken(val)) return true;
  // Simple RFC-ish check — not exhaustive, intentionally permissive
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
}

/** Split a raw string (e.g. from paste) into individual address candidates */
function splitRaw(raw: string): string[] {
  return raw
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// ─────────────────────────────────────────────────────────────────────────────
// Chip
// ─────────────────────────────────────────────────────────────────────────────

interface ChipProps {
  value: string;
  onRemove: () => void;
  disabled?: boolean;
}

function Chip({ value, onRemove, disabled }: ChipProps) {
  const isToken = isMergeToken(value);
  const isValid = isValidEntry(value);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium leading-relaxed',
        isToken
          ? 'bg-primary/10 text-primary border border-primary/20'
          : isValid
            ? 'bg-muted text-foreground border border-border'
            : 'bg-destructive/10 text-destructive border border-destructive/20',
      )}
    >
      {value}
      {!disabled && (
        <button
          type="button"
          onClick={onRemove}
          className="rounded-full hover:bg-black/10 p-0.5 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-label={`Remove ${value}`}
        >
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EmailAddressInput
// ─────────────────────────────────────────────────────────────────────────────

export interface EmailAddressInputProps {
  /** Current list of committed addresses / tokens */
  value: string[];
  /** Called whenever the list changes */
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  /** Whether to show validation error styling when a non-valid value is typed */
  showInvalid?: boolean;
}

export function EmailAddressInput({
  value,
  onChange,
  placeholder = 'Add address…',
  disabled = false,
  id,
  className,
}: EmailAddressInputProps) {
  const [inputVal, setInputVal] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Commit whatever is in the text input as a chip
  const commitInput = () => {
    const trimmed = inputVal.trim().replace(/,+$/, '');
    if (!trimmed) return;
    const candidates = splitRaw(trimmed);
    const next = [
      ...value,
      ...candidates.filter((c) => !value.includes(c)),
    ];
    onChange(next);
    setInputVal('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (inputVal.trim()) {
        e.preventDefault();
        commitInput();
      }
      return;
    }
    // Comma or semicolon as delimiter
    if (e.key === ',' || e.key === ';') {
      e.preventDefault();
      commitInput();
      return;
    }
    // Backspace on empty input removes the last chip
    if (e.key === 'Backspace' && inputVal === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const raw = e.clipboardData.getData('text');
    const candidates = splitRaw(raw);
    if (candidates.length > 1) {
      // Multi-address paste — swallow and create chips immediately
      e.preventDefault();
      const next = [
        ...value,
        ...candidates.filter((c) => !value.includes(c)),
      ];
      onChange(next);
      setInputVal('');
    }
    // Single address — let it land in the input naturally, user can commit with Enter
  };

  const removeAt = (idx: number) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
    inputRef.current?.focus();
  };

  return (
    <div
      className={cn(
        // Mirrors shadcn Input styling
        'flex flex-wrap gap-1.5 min-h-9 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm',
        'ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {/* Chips */}
      {value.map((addr, idx) => (
        <Chip
          key={`${addr}-${idx}`}
          value={addr}
          onRemove={() => removeAt(idx)}
          disabled={disabled}
        />
      ))}

      {/* Text input */}
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={inputVal}
        onChange={(e) => setInputVal(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commitInput}
        onPaste={handlePaste}
        placeholder={value.length === 0 ? placeholder : ''}
        disabled={disabled}
        className={cn(
          'flex-1 min-w-[140px] bg-transparent outline-none placeholder:text-muted-foreground text-sm',
          'disabled:cursor-not-allowed',
        )}
        autoComplete="off"
        spellCheck={false}
      />
    </div>
  );
}
